import React, { useState } from 'react';
import { Award, Clock, MapPin, MonitorPlay, Users, MessageSquareText } from 'lucide-react';
import usePageContent from '../hooks/usePageContent';
import TrainingForm from '../components/TrainingForm';
import useSEO from '../hooks/useSEO';

const PRICING = [
    { icon: MessageSquareText, title: 'Your goals', text: 'Tell us what you want to learn — we build the course around it.' },
    { icon: MapPin, title: 'On-site or remote', text: 'At your facility with your equipment, or live online.' },
    { icon: Users, title: 'Your team size', text: 'One operator or a whole crew — priced per group, not per seat.' },
];

const CourseCard = ({ c, picked, onPick }) => (
    <div className={`border bg-white p-6 flex flex-col transition-colors ${picked ? 'border-brand-primary' : 'border-neutral-200'}`} data-testid={`training-course-${c.id}`}>
        <Award className="h-6 w-6 text-brand-primary" />
        <h3 className="mt-4 font-display font-black uppercase text-xl text-neutral-900">{c.name}</h3>
        {c.duration && <div className="mt-1 text-sm text-neutral-500 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />Typically {c.duration}</div>}
        <p className="mt-3 text-sm text-neutral-600 flex-1">{c.description}</p>
        <div className="mt-4 text-sm text-neutral-500">Priced to your needs</div>
        <button type="button" onClick={onPick} className={`mt-4 px-4 py-2.5 label-caps text-xs border transition-colors ${picked ? 'bg-neutral-900 text-on-dark border-neutral-900' : 'border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white'}`} data-testid={`training-course-pick-${c.id}`}>
            {picked ? 'Added to my request' : 'I\u2019m interested'}
        </button>
    </div>
);

const Training = () => {
    const content = usePageContent();
    const courses = content?.courses || [];
    const [picked, setPicked] = useState([]);
    useSEO({ title: 'Dry Ice Blasting Training & Certification', description: 'Custom dry ice blasting operator training and certification — on-site or remote, built around your team, machine and goals.' });
    const pick = (id) => {
        if (!picked.includes(id)) setPicked([...picked, id]);
        document.getElementById('request')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div data-testid="training-page">
            <section className="border-b border-neutral-200 bg-neutral-50">
                <div className="container-page pt-32 pb-14">
                    <div className="label-caps text-brand-primary mb-6">/ Training & certification</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl text-neutral-900">Learn it right.<br />Your way.</h1>
                    <p className="mt-6 text-neutral-600 max-w-2xl" data-testid="training-intro">{content?.training_intro}</p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <a href="#request" className="bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-7 py-4 label-caps transition-colors" data-testid="training-hero-cta">Tell us what you want to learn</a>
                        <span className="inline-flex items-center gap-2 text-sm text-neutral-600 px-2"><MonitorPlay className="h-4 w-4 text-brand-primary" /> On-site or remote</span>
                    </div>
                </div>
            </section>

            <section className="container-page py-16">
                <div className="label-caps text-brand-primary mb-3">/ 01 — How pricing works</div>
                <h2 className="font-display font-black uppercase text-3xl md:text-4xl text-neutral-900 mb-8">Every training is different</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="training-pricing">
                    {PRICING.map(({ icon: Icon, title, text }) => (
                        <div key={title} className="border border-neutral-200 p-6 flex gap-4">
                            <Icon className="h-6 w-6 text-brand-primary flex-shrink-0" />
                            <div><div className="font-display font-bold uppercase text-neutral-900">{title}</div><p className="mt-1 text-sm text-neutral-600">{text}</p></div>
                        </div>
                    ))}
                </div>
            </section>

            {courses.length > 0 && (
                <section className="border-t border-neutral-200 bg-neutral-50">
                    <div className="container-page py-16">
                        <div className="label-caps text-brand-primary mb-3">/ 02 — Popular programs</div>
                        <h2 className="font-display font-black uppercase text-3xl md:text-4xl text-neutral-900 mb-8">Start from a program</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="training-courses">
                            {courses.map((c) => <CourseCard key={c.id} c={c} picked={picked.includes(c.id)} onPick={() => pick(c.id)} />)}
                        </div>
                    </div>
                </section>
            )}

            <section className="container-page py-16 max-w-4xl" id="request">
                <div className="label-caps text-brand-primary mb-3">/ 03 — Request training</div>
                <h2 className="font-display font-black uppercase text-3xl md:text-4xl text-neutral-900 mb-3">Tell us what you need</h2>
                <p className="text-neutral-600 mb-8">We reply within one business day with a training plan and price.</p>
                <TrainingForm courses={courses} picked={picked} setPicked={setPicked} />
            </section>
        </div>
    );
};

export default Training;
